import { FieldMetaProps } from "formik"
import { DateTime } from "luxon"
import { Frequency, RRule, rrulestr } from "rrule"
import type { AnyObject } from "yup"
import * as Yup from "yup"

import type { AppLocalization } from "../../../../../modules/localization/appLocalization"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { MeetingServiceOrderResource } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { PaymentMethodType } from "../../../../../modules/payments/paymentTypes"
import { ResourceItem } from "../../createOrderTypes"
import {
    CreateUpdateBookingFormValues,
    CreateUpdateBookingResourceTableSetups,
} from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { InitialValuesState } from "../resource/ResourceBookingDraftContext"

import {
    ConfigSection,
    DynamicAccountingKey,
    DynamicConfigField,
    DynamicConfigKey,
    ExtendedDynamicConfigKey,
    InputField,
    MeetingServiceConfig,
    MeetingServiceTabValues,
    PaymentMethodOption,
    ValidationType,
} from "./formTypes"
import { AccountingDimension } from "../../../dialogs/receiptConfirmOrderDialog/receiptConfirmOrderDialogTypes"

export const INVALID_DELIVERIES = "INVALID_DELIVERIES"

// `inputField.hidden` keeps a field out of the UI while its predefined value still reaches the API.
export function isHiddenConfigField(field: DynamicConfigField): boolean {
    return field.inputField?.hidden === true
}

// The single visibility predicate for every config-driven renderer.
export function isRenderableConfigField(field: DynamicConfigField): boolean {
    return Boolean(field.active) && !isHiddenConfigField(field)
}

// Only these types hold a plain scalar that `predefined.value` can express. Object-shaped fields
// ("component", "autocomplete", "multiple_select") are built by dedicated mappers, so the config
// must not seed them directly. Allow-list rather than deny-list: `InputField["type"]` accepts any
// string, and an unknown type must keep its existing behaviour.
const LOCKABLE_INPUT_TYPES: ReadonlySet<string> = new Set([
    "textField",
    "number",
    "checkbox",
    "toogle",
    "select",
    "singleSelect",
    "label",
])

// Keys whose Formik value is an object, an array or a DateTime. The type check above is not enough
// for them: FieldRenderer dispatches these by key, not by `inputField.type`, so a config is free to
// declare e.g. `type: "select"` on SATELLITES while the value stays a SatelliteDetails object.
// Seeding those from `predefined.value` would replace the object with a scalar.
const NON_SCALAR_KEYS: ReadonlySet<string> = new Set<string>([
    DynamicConfigKey.RECURRENCY,
    DynamicConfigKey.MULTIPLE_DELIVERIES,
    DynamicConfigKey.REGISTER_PARTICIPANTS,
    DynamicConfigKey.ADD_PARTICIPANTS_FORM,
    DynamicConfigKey.MEETING_RESPONSIBLE,
    DynamicConfigKey.BUYER_PARTIES,
    DynamicConfigKey.PAYMENT_METHOD,
    DynamicConfigKey.SATELLITES,
    DynamicConfigKey.TABLE_ARANGEMENTS,
    DynamicConfigKey.MEETING_TIME,
    DynamicConfigKey.MEETING_START_TIME,
    DynamicConfigKey.MEETING_END_TIME,
])

// A hidden field with a predefined scalar is owned by the config: the user cannot edit it, so the
// config value must win over anything restored from the order or from the outer form.
export function isConfigLockedField(field: DynamicConfigField): boolean {
    const inputField = field.inputField

    return (
        Boolean(field.active) &&
        isHiddenConfigField(field) &&
        inputField?.predefined?.value !== undefined &&
        LOCKABLE_INPUT_TYPES.has(inputField.type) &&
        !NON_SCALAR_KEYS.has(field.key) &&
        !isAccountingDimensionKey(field.key)
    )
}

type YupString = Yup.StringSchema<string | undefined, AnyObject, string | undefined>
type YupNumber = Yup.NumberSchema<number | undefined, AnyObject, number | undefined>
type YupBoolean = Yup.BooleanSchema<boolean | undefined, AnyObject, boolean | undefined>

export function buildFieldSchema(field: InputField | undefined, label: string, l10n: AppLocalization): Yup.Schema<any> {
    if (!field) {
        return Yup.mixed()
    }

    let schema: Yup.Schema<any>

    switch (field?.type) {
        case "textField":
            schema = Yup.string()
            break

        case "checkbox":
            schema = Yup.boolean()
            break

        case "number":
            schema = Yup.number()
            break

        case "dateTimePicker":
            schema = Yup.date().typeError(l10n.getString("create-order-validation-date-invalid", { label }))
            break

        default:
            schema = Yup.mixed()
            break
    }

    if (field?.isRequired) {
        if (field.type === "checkbox") {
            schema = (schema as YupBoolean).oneOf(
                [true],
                l10n.getString("create-order-validation-must-be-checked", { label }),
            )
        } else {
            schema = schema.required(l10n.getString("create-order-validation-required", { label }))
            if (field.type === "number") {
                schema = (schema as YupNumber).min(1, l10n.getString("create-order-validation-min", { label, min: 1 }))
            }
        }
    }

    if (field?.type === "number" && typeof field.maxValue === "number") {
        schema = (schema as YupNumber).max(
            field.maxValue,
            l10n.getString("create-order-validation-max", { label, max: field.maxValue }),
        )
    }

    if (field?.validations && Array.isArray(field.validations)) {
        for (const rule of field.validations) {
            switch (rule.type) {
                case ValidationType.REQUIRED:
                    schema = schema.required(
                        rule.message || l10n.getString("create-order-validation-required", { label }),
                    )
                    break

                case ValidationType.IS_TRUE:
                    if (field.type === "checkbox") {
                        schema = (schema as YupBoolean).isTrue(
                            rule.message || l10n.getString("create-order-validation-must-be-true", { label }),
                        )
                    }
                    break

                case ValidationType.EMAIL:
                    if (field.type === "textField") {
                        schema = (schema as YupString).email(
                            rule.message || l10n.getString("create-order-validation-email-invalid", { label }),
                        )
                    }
                    break

                case ValidationType.REGEX:
                    if (field.type === "textField" && rule.regex) {
                        schema = (schema as YupString).matches(
                            new RegExp(rule.regex),
                            rule.message || l10n.getString("create-order-validation-format-invalid", { label }),
                        )
                    }
                    break

                default:
                    // Handle unknown validation types gracefully
                    console.warn(`Unsupported validation type '${(rule as any).type}' for field:`, field)
                    break
            }
        }
    }

    if (!field.isRequired) {
        schema = schema.notRequired().nullable()
    }

    return schema
}

export function isAccountingDimensionKey(key: string): key is DynamicAccountingKey {
    return /^ACCOUNTING_DIMENSION_\d+$/.test(key)
}

// API contract for order create/update: send dimension object with id and a cost center object inside.
// cost center obj will have either a value or id depending on if the input field is a text field
//  or select. value is the selected/typed cost-center code kept as a string (codes can be non-numeric, e.g. "HR",
// or carry leading zeros, so it must never be converted to a number).
function createDimensionObject(dimensionId: number, rawValue: unknown): AccountingDimension | null {
    if (typeof rawValue === "string" || typeof rawValue === "number") {
        const text = String(rawValue).trim()
        const value = text || null
        return value ? { id: dimensionId, costCenter: { value } } : null
    }

    if (typeof rawValue === "object" && rawValue !== null && "id" in rawValue) {
        const { id: rawId } = rawValue
        if (typeof rawId === "number") {
            const id = rawId
            return id ? { id: dimensionId, costCenter: { id } } : null
        } else if (typeof rawId === "string" && rawId && rawId.trim()) {
            const id = Number(rawId.trim())
            return { id: dimensionId, costCenter: { id } }
        }
    }

    return null
}

export function extractAccountingDimensions(list: MeetingServiceTabValues): AccountingDimension[] {
    return Object.entries(list)
        .filter((entry): entry is [DynamicAccountingKey, unknown] => isAccountingDimensionKey(entry[0]))
        .map(([key, val]) => {
            const dimensionId = Number(key.split("_")[2])
            if (!Number.isInteger(dimensionId) || dimensionId <= 0) return null

            return createDimensionObject(dimensionId, val)
        })
        .filter((d): d is AccountingDimension => Boolean(d))
}

export function extractAccountingDimensionsWithNames(orderDetails: MeetingServiceTabValues) {
    return Object.entries(orderDetails)
        .filter(([key]) => isAccountingDimensionKey(key))
        .map(([key, val]) => {
            if (!val) return null

            if (typeof val === "object" && "id" in val && "name" in val) {
                return {
                    name: val.name,
                    dimensionName: val.dimensionName || "",
                }
            }

            return {
                name: String(val),
                dimensionName: val.dimensionName || "",
            }
        })
        .filter((d): d is { name: string; dimensionName: string } => Boolean(d))
}

export type AutocompleteFormikFieldError = {
    error: boolean
    helperText: string | undefined
}
export function getFormikFieldError(meta: FieldMetaProps<any>, submitCount: number): AutocompleteFormikFieldError {
    const showError = Boolean(meta.error) && (meta.touched || submitCount > 0)

    return {
        error: showError,
        helperText: showError ? meta.error : undefined,
    }
}

export function getBookingResourceWithTableSetup(
    resources: MeetingServiceOrderResource[],
): CreateUpdateBookingResourceTableSetups[] {
    return resources.map((res) => ({
        id: res.id,
        ...(res.room?.tableSetup && { tableSetups: [{ key: res.room.tableSetup.key }] }),
    }))
}

export const sanitizeAndLimitInput = (rawValue: string, maxLen: number, maxValue?: number): string => {
    const digitsOnly = rawValue.replace(/[^0-9]/g, "")
    const limited = digitsOnly.slice(0, maxLen)
    if (typeof maxValue === "number" && Number(limited) > maxValue) {
        return maxValue.toString()
    }
    return limited
}

export const bookingFormValuesToDynamicConfigKey = (
    val: CreateUpdateBookingFormValues,
    initVal: Record<ExtendedDynamicConfigKey | string, any>,
): Record<ExtendedDynamicConfigKey | string, any> => {
    const resourceIds = val.meetingDetails.resourceIds.map((num) => String(num))

    return {
        [DynamicConfigKey.MEETING_NAME]:
            val.meetingDetails?.meetingName || initVal[DynamicConfigKey.MEETING_NAME] || "",
        [DynamicConfigKey.REGISTER_PARTICIPANTS]:
            val.meetingDetails?.attendees || initVal[DynamicConfigKey.REGISTER_PARTICIPANTS] || [],
        [DynamicConfigKey.PRIVATE_MEETING]: val.meetingDetails?.isPrivate,
        [DynamicConfigKey.EXTERNAL_MEETING]: val.meetingDetails?.isExternal,
        [DynamicConfigKey.MEETING_RESPONSIBLE]:
            val.organizers?.id && val.organizers.id === initVal[DynamicConfigKey.MEETING_RESPONSIBLE]?.id
                ? initVal[DynamicConfigKey.MEETING_RESPONSIBLE]
                : val.organizers,
        ["event"]: { ...initVal["event"], resourceIds: resourceIds },
        ["fullResourcesList"]: initVal["fullResourcesList"].filter((res: ResourceItem) =>
            resourceIds.includes(String(res.id)),
        ),
        [DynamicConfigKey.SERVICE_CENTER_MESSAGE]:
            val.messageToServiceCenter || initVal[DynamicConfigKey.SERVICE_CENTER_MESSAGE] || "",
    }
}

export const generateConfirmBookingInitialValues = (
    formValues: Record<ExtendedDynamicConfigKey | string, any>,
    id?: number | null,
): InitialValuesState | null => {
    return {
        id: id || null,
        meetingName: formValues[DynamicConfigKey.MEETING_NAME],
        organizers: formValues[DynamicConfigKey.MEETING_RESPONSIBLE],
        attendees: formValues[DynamicConfigKey.REGISTER_PARTICIPANTS],
        isPrivate: formValues[DynamicConfigKey.PRIVATE_MEETING],
        isExternal: formValues[DynamicConfigKey.EXTERNAL_MEETING],
        messageToServiceCenter: formValues[DynamicConfigKey.SERVICE_CENTER_MESSAGE],
        resources: formValues["resources"],
    }
}

export function parseRRuleToCustomObject(icalString: string, dateTimeFactory: DateTimeFactory) {
    const sanitized = icalString
        .split("\n")
        .filter((line) => !line.startsWith("DTEND"))
        .join("\n")
    const rrule: RRule = rrulestr(sanitized)
    const until = rrule.options.until

    return {
        repeatType: rrule.options.freq === Frequency.DAILY ? "day" : "week",
        repeatInterval: rrule.options.interval,
        startDate: dateTimeFactory.fromISO(dateTimeFactory.fromJSDate(rrule.options.dtstart).toISO()),
        endType: until ? "on" : "never",
        endDate: until ? dateTimeFactory.fromISO(dateTimeFactory.fromJSDate(until).toISO()) : undefined,
    }
}

export function findGroupByFieldKey(sections: ConfigSection[], key: string) {
    return sections
        .flatMap((section) => section.groups)
        .find((group) => group.fields.some((field: DynamicConfigField) => field.key === key))
}

export const defaultPaymentMethod: PaymentMethodOption = {
    value: PaymentMethodType.INVOICE,
    icon: { type: "FONT_AWESOME", spec: "fa-light fa-file-lines" },
}

export function ensurePaymentMethod(config: MeetingServiceConfig): MeetingServiceConfig {
    const PM_KEY = DynamicConfigKey.PAYMENT_METHOD

    // If the PAYMENT_METHOD group exists, activate and normalize paymentMethods.
    const targetGroup = findGroupByFieldKey(config.sections, PM_KEY)
    if (targetGroup) {
        const updatedSections = config.sections.map((section) => ({
            ...section,
            groups: section.groups.map((group) =>
                group === targetGroup
                    ? {
                          ...group,
                          fields: group.fields.map((f) => {
                              if (f.key !== PM_KEY) return f

                              // always activate
                              const base = { ...f, active: true }

                              // if there is no inputField, create it using the appropriate method
                              if (!f.inputField) {
                                  return {
                                      ...base,
                                      inputField: {
                                          type: "component",
                                          isRequired: true,
                                          paymentMethods: [defaultPaymentMethod],
                                      },
                                  }
                              }

                              const pm = f.inputField.paymentMethods ?? []
                              const hasInvoice = pm.some((m) => m.value === PaymentMethodType.INVOICE)
                              const invoiceEntry = hasInvoice
                                  ? pm.find((m) => m.value === PaymentMethodType.INVOICE)!
                                  : defaultPaymentMethod

                              // IMPORTANT: leave ONLY INVOICE (or add it if it was not there)
                              return {
                                  ...base,
                                  inputField: {
                                      ...f.inputField,
                                      paymentMethods: [invoiceEntry],
                                  },
                              }
                          }),
                      }
                    : group,
            ),
        }))
        return { ...config, sections: updatedSections }
    }

    // There is no field — add it to the “lower” group of the last section, first in fields
    const newField: DynamicConfigField = {
        key: PM_KEY,
        active: true,
        label: "Payment method",
        description: "",
        inputField: {
            type: "component",
            isRequired: true,
            paymentMethods: [defaultPaymentMethod],
        },
    }

    if (config.sections.length === 0) {
        return {
            ...config,
            sections: [{ title: "Payment", groups: [{ fields: [newField] }] }],
        }
    }

    const lastIndex = config.sections.length - 1
    const lastSection = config.sections[lastIndex]

    let insertGroupIndex = lastSection.groups.findIndex((g) => g.fields && g.fields.length > 0)
    if (insertGroupIndex === -1 && lastSection.groups.length > 0) {
        insertGroupIndex = 0
    }

    let updatedLastSection: ConfigSection
    if (lastSection.groups.length === 0) {
        updatedLastSection = { ...lastSection, groups: [{ fields: [newField] }] }
    } else {
        const updatedGroups = lastSection.groups.map((g, i) =>
            i === insertGroupIndex ? { ...g, fields: [newField, ...g.fields] } : g,
        )
        updatedLastSection = { ...lastSection, groups: updatedGroups }
    }

    return {
        ...config,
        sections: [...config.sections.slice(0, lastIndex), updatedLastSection, ...config.sections.slice(lastIndex + 1)],
    }
}
